import axios from 'axios'

const isLocalhost =
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1' ||
  window.location.hostname === '192.168.1.10'

const API_URL =
  process.env.REACT_APP_API_URL ||
  (isLocalhost
    ? 'http://localhost:8090/api'
    : 'https://tribulum.redirectme.net/api')

const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

let refreshPromise = null
let cachedRememberMe = localStorage.getItem('rememberMe') === 'true'
let cachedAccessToken = null

export const setRememberCache = (value) => {
  cachedRememberMe = value
  cachedAccessToken = null
}

export const invalidateTokenCache = () => {
  cachedAccessToken = null
}

const getCurrentStorage = () => {
  return cachedRememberMe ? localStorage : sessionStorage
}

const getAccessToken = () => {
  if (cachedAccessToken !== null) return cachedAccessToken
  cachedAccessToken = getCurrentStorage().getItem('accessToken')
  return cachedAccessToken
}

const refreshAccessToken = async () => {
  try {
    const storage = getCurrentStorage()
    const refreshToken = storage.getItem('refreshToken')

    if (!refreshToken) {
      console.error('[API] No hay refresh token disponible')
      throw new Error('No hay refresh token disponible')
    }

    const response = await apiClient.post('/auth/refresh', {
      refreshToken,
      deviceInfo: navigator.userAgent,
    })

    const { accessToken, refreshToken: newRefreshToken } = response.data

    storage.setItem('accessToken', accessToken)
    if (newRefreshToken) {
      storage.setItem('refreshToken', newRefreshToken)
    }
    cachedAccessToken = accessToken

    return accessToken
  } catch (error) {
    console.error('[API] Error en refresh token:', error)
    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')
    sessionStorage.removeItem('accessToken')
    sessionStorage.removeItem('refreshToken')
    throw error
  }
}

apiClient.interceptors.request.use(
  (config) => {
    const token = getAccessToken()

    if (token && !config.url.includes('/auth/')) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Log de la solicitud
    console.log(`[API] -> ${config.method?.toUpperCase()} ${config.url}`, {
      headers: config.headers,
      data: config.data,
    })

    return config
  },
  (error) => {
    console.error('[API] Error en interceptor de request:', error)
    return Promise.reject(error)
  },
)

apiClient.interceptors.response.use(
  (response) => {
    // Log de la respuesta
    console.log(
      `[API] <- ${response.config.method?.toUpperCase()} ${response.config.url}`,
      {
        status: response.status,
        data: response.data,
      },
    )
    return response
  },
  async (error) => {
    const originalRequest = error.config

    console.error(
      `[API] Error en ${originalRequest?.method?.toUpperCase()} ${originalRequest?.url}:`,
      {
        status: error.response?.status,
        data: error.response?.data,
      },
    )

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url.includes('/auth/')
    ) {
      originalRequest._retry = true

      try {
        if (!refreshPromise) {
          refreshPromise = refreshAccessToken().finally(() => {
            refreshPromise = null
          })
        }

        const newToken = await refreshPromise
        originalRequest.headers.Authorization = `Bearer ${newToken}`

        return apiClient(originalRequest)
      } catch (refreshError) {
        console.error('[API] Error al refrescar token:', refreshError)

        // Solo redirigir si había token (sesión real expirada), no si nunca hubo sesión
        const storage = getCurrentStorage()
        const hadToken = storage.getItem('refreshToken')
        storage.removeItem('accessToken')
        storage.removeItem('refreshToken')

        if (hadToken && !window.location.pathname.includes('/login')) {
          window.location.href = '/login'
        }
        return Promise.reject(refreshError)
      }
    }

    return Promise.reject(error)
  },
)

export default apiClient
